import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailIcon from "@mui/icons-material/Email";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FileCopy from "@mui/icons-material/FileCopy";
import GroupIcon from "@mui/icons-material/Group";
import InboxIcon from "@mui/icons-material/Inbox";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";import SettingsIcon from "@mui/icons-material/Settings";
import SettingsAccessibility from "@mui/icons-material/SettingsAccessibility";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TimelineIcon from "@mui/icons-material/Timeline";
import TodayIcon from "@mui/icons-material/Today";
import WorkIcon from "@mui/icons-material/Work";
import { calendarRoute } from "calendar/calendar-navigation";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton, { type ListItemButtonProps } from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/system";
import attorneyHubMarketplaceLogo from "_shared/_views/assets/attorney-hub-marketplace-logo.svg";
import { isRouteWithinRoot } from "_shared/_views/route-access-guard";
import useSessionUser from "permissions/session/use-session-user";
import React, { type ReactNode, useState } from "react";
import { Link, useLocation } from "react-router";
import { useSession } from "users/__session/session-context";
import { SESSION_RECORDING_UNMASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

/** MUI's toolbar heights, which the app bar takes: 56px on a phone, 64px from `sm` up. */
const APP_BAR_HEIGHT = 56;
const APP_BAR_HEIGHT_FROM_SM = 64;
const CompactNavigationContext = React.createContext(false);

function NavigationItem(props: ListItemButtonProps & {
  to?: string;
  href?: string;
  target?: string;
}) {
  const compact = React.useContext(CompactNavigationContext);
  if (!compact) return <ListItemButton {...props} />;

  const label = React.Children.toArray(props.children)
    .find(child => React.isValidElement<{ primary?: string }>(child) && child.props.primary);
  const title = React.isValidElement<{ primary?: string }>(label) ? label.props.primary : undefined;
  return (
    <Tooltip title={title} placement="right">
      <ListItemButton {...props} aria-label={title} />
    </Tooltip>
  );
}

const NavDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition" && prop !== "temporary" && prop !== "compact",
})<{ temporary: boolean; compact: boolean }>(({ temporary, compact, theme }) => ({
  width: temporary ? "auto" : compact ? "64px" : "17rem",
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    paddingTop: theme.spacing(2),
    position: "absolute",
    width: compact ? "64px" : "17rem",
    ...(compact && {
      "& .MuiListItemText-root": { display: "none" },
      "& .MuiListItemButton-root": { justifyContent: "center", minHeight: 48 },
      "& .MuiListItemIcon-root": { minWidth: 0 },
      "& .MuiListItemButton-root > .MuiSvgIcon-root": { display: "none" },
      "& .MuiCollapse-root": { paddingLeft: 0 },
    }),
    // One paper rule, not a second one spread in below: two equal keys in one object are one key, and
    // the later would drop the width and padding above.
    ...(temporary && {
      // The items keep the height they had when the paper started behind the app bar.
      // Below the banners too, which push the app bar down from the top of the window.
      paddingTop: theme.spacing(1),
      height: `calc(100% - ${APP_BAR_HEIGHT}px - ${shellTopBannersHeight()})`,
      top: `calc(${APP_BAR_HEIGHT}px + ${shellTopBannersHeight()})`,
      [theme.breakpoints.up("sm")]: {
        height: `calc(100% - ${APP_BAR_HEIGHT_FROM_SM}px - ${shellTopBannersHeight()})`,
        top: `calc(${APP_BAR_HEIGHT_FROM_SM}px + ${shellTopBannersHeight()})`,
      },
    }),
  },
  /**
   * The hamburger closes the drawer it opened (#6041).
   *
   * A temporary drawer is a modal: MUI stacks it above the app bar and makes the rest of the page
   * inert while it is open, app bar included. Its paper ran to the top of the screen under the
   * hamburger, so a second tap passed through the inert button onto the paper and did nothing. The
   * paper starts below the app bar now, so that tap reaches the backdrop, and a backdrop tap closes
   * the drawer. At the drawer layer, under the app bar's `drawer + 1`, the app bar is drawn over it as
   * well; dialogs, at the modal layer, still cover both.
   */
  ...(temporary && { zIndex: theme.zIndex.drawer }),
}));
const NavColumn = styled("div")<{ temporary: boolean }>((props) => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  // A temporary drawer's paper already starts below the app bar; the permanent one starts behind it.
  marginTop: props.temporary ? 0 : props.theme.spacing(8),
  minHeight: 0,
}));
/**
 * Only the links scroll. What the shell puts above them stays outside, because a scrollbar here is
 * 20px wide (`index.css`) and took that from the assistant's entry whenever the links overflowed,
 * wrapping its label onto a second line.
 */
const NavContainer = styled("div")({
  flex: "1 1 auto",
  minHeight: 0,
  overflow: "auto",
  position: "relative",
});
const MarketplaceLink = styled(Link)((props) => ({
  opacity: 0.6,
  textAlign: "center",
  transition: "opacity 0.5s ease-out",
  "&:hover": {
    opacity: 1,
  },
  "& img": {
    margin: props.theme.spacing(2, "auto"),
    width: "75%",
    [props.theme.breakpoints.down("sm")]: {
      width: "66%",
    },
  },
}));

type SideNavProps = {
  /** Icon rail beside the assistant in the permanent desktop navigation. */
  compact?: boolean;
  showNav: boolean;
  navMenuClosed: () => void;
  /**
   * Drawn above the links where the nav is a column (`lg` and up), and nowhere else. The shell puts
   * the assistant's entry here (#6128); the nav sheet below `lg` leaves it to the bottom bar.
   */
  leading?: ReactNode;
};

export default function SideNav(props: Readonly<SideNavProps>) {
  const location = useLocation();
  const session = useSession();
  const showTemporaryDrawer = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  // Each entry below asks the user one question, and it is the same question `RouteAccessGuard`
  // asks of the route behind the link — so the nav cannot offer a page the guard will refuse
  // (issue #4651).
  const user = useSessionUser();

  const marketplaceMenuOpen = location.pathname.startsWith("/marketplace");
  // Open when the reader is in Attorney Hub's own knowledge base, unless they have said otherwise.
  // Held as an override rather than as the state itself: this nav is not remounted on navigation,
  // so seeding state from the route once left Support collapsed while one of its own pages was
  // open - which is what the Help button does. The override is still needed because Support also
  // contains a mailto, which has no route to match against.
  const [supportMenuChoice, setSupportMenuChoice] = useState<boolean | undefined>(undefined);
  const supportMenuOpen =
    supportMenuChoice ?? location.pathname.startsWith("/knowledge-base/attorney-hub");

  function handleNavClose(temporary: boolean) {
    if (temporary) {
      props.navMenuClosed();
    }
  }

  function drawer(temporary: boolean) {
    const compact = Boolean(props.compact && !temporary);
    if (session.isRestrictedOnboarding) {
      return (
        <NavDrawer
          closeAfterTransition={false}
          temporary={temporary}
          compact={compact}
          variant={temporary ? "temporary" : "permanent"}
          anchor={temporary ? "right" : "left"}
          open={temporary ? props.showNav : true}
          onClose={() => {
            if (temporary) props.navMenuClosed();
          }}
          ModalProps={{ keepMounted: true }}
        >
          <NavColumn temporary={temporary} className={SESSION_RECORDING_UNMASK_CLASS}>
            {!temporary && props.leading}
            <NavContainer>
              <List>
                {user?.canAccessProposals() && (
                  <NavigationItem
                    to="/proposals"
                    selected={location.pathname.startsWith("/proposals")}
                    component={Link}
                    onClick={() => handleNavClose(temporary)}
                  >
                    <ListItemIcon><AssignmentIcon /></ListItemIcon>
                    <ListItemText primary="Proposals" />
                  </NavigationItem>
                )}
                <NavigationItem
                  to="/help"
                  selected={location.pathname.startsWith("/help")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon><ContactSupportIcon /></ListItemIcon>
                  <ListItemText primary="Help" />
                </NavigationItem>
                <NavigationItem href="mailto:support@attorneyhub.com" target="_top" component="a">
                  <ListItemIcon><ContactSupportIcon /></ListItemIcon>
                  <ListItemText primary="Support" />
                </NavigationItem>
              </List>
            </NavContainer>
          </NavColumn>
        </NavDrawer>
      );
    }

    return (
      <NavDrawer
        closeAfterTransition={false}
        temporary={temporary}
        compact={compact}
        variant={temporary ? "temporary" : "permanent"}
        anchor={temporary ? "right" : "left"}
        open={temporary ? props.showNav : true}
        onClose={() => {
          if (temporary) {
            props.navMenuClosed();
          }
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        <NavColumn temporary={temporary} className={SESSION_RECORDING_UNMASK_CLASS}>
          {!temporary && props.leading}
          <NavContainer>
            <List>
              <NavigationItem
                to="/dashboard"
                selected={location.pathname === "/dashboard"}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </NavigationItem>
              {user?.canAccessCommunications() && (
                <NavigationItem
                  to="/communications"
                  selected={location.pathname.startsWith("/communications")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <InboxIcon />
                  </ListItemIcon>
                  <ListItemText primary="Communications" />
                </NavigationItem>
              )}
              {user?.canAccessWork() && (
                <NavigationItem
                  to="/work"
                  selected={location.pathname.startsWith("/work")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <WorkIcon />
                  </ListItemIcon>
                  <ListItemText primary="Work" />
                </NavigationItem>
              )}
              {user?.canAccessProposals() && (
                <NavigationItem
                  to="/proposals"
                  selected={location.pathname.startsWith("/proposals")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <AssignmentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Proposals" />
                </NavigationItem>
              )}
              {user?.canAccessTeams() && (
                <NavigationItem
                  to="/teams"
                  selected={location.pathname.startsWith("/teams")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <GroupIcon />
                  </ListItemIcon>
                  <ListItemText primary="Teams" />
                </NavigationItem>
              )}
              {user?.canAccessFeeSchedules() && (
                <NavigationItem
                  to="/fee-schedules"
                  selected={location.pathname.startsWith("/fee-schedules")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <AccountBalanceIcon />
                  </ListItemIcon>
                  <ListItemText primary="Fee Schedules" />
                </NavigationItem>
              )}
              {/*
                Everyone's: the list holds only the documents its reader may read, each row filtered on
                the server, so the page needs no gate of its own (#6720).
              */}
              <NavigationItem
                to="/documents"
                selected={location.pathname.startsWith("/documents")}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <FileCopy />
                </ListItemIcon>
                <ListItemText primary="Documents" />
              </NavigationItem>
              {user?.canAccessKnowledgeBase() && (
                // Plain "Knowledge Base": this is the signed-in entity's own, and the reader knows
                // which entity they are signed in as. Naming it after the entity read as a near
                // duplicate of the entry under Support, which is now "Support Knowledge Base" and
                // named for what it is for rather than for who owns it.
                <NavigationItem
                  to="/knowledge-base"
                  selected={
                    location.pathname.startsWith("/knowledge-base")
                    && !location.pathname.startsWith("/knowledge-base/attorney-hub")
                  }
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <MenuBookIcon />
                  </ListItemIcon>
                  <ListItemText primary="Knowledge Base" />
                </NavigationItem>
              )}
              {user?.canAccessMarketplace() && (
                <>
                  <NavigationItem
                    to="/marketplace"
                    selected={location.pathname.startsWith("/marketplace")}
                    component={Link}
                    onClick={() => handleNavClose(temporary)}
                  >
                    <ListItemIcon>
                      <StorefrontIcon />
                    </ListItemIcon>
                    <ListItemText primary="Marketplace" />
                    {marketplaceMenuOpen ? <ExpandLess /> : <ExpandMore />}
                  </NavigationItem>
                  <Collapse
                    in={marketplaceMenuOpen}
                    timeout="auto"
                    unmountOnExit
                    sx={{ backgroundColor: "#FAFAFA", pl: 2 }}
                  >
                    <List component="div" disablePadding>
                      {user?.canSearchMarketplace() && (
                        <NavigationItem
                          to="/marketplace"
                          selected={new RegExp(/\/marketplace[^/]*$/).exec(location.pathname) !== null}
                          component={Link}
                          onClick={() => handleNavClose(temporary)}
                        >
                          <ListItemIcon>
                            <SearchIcon />
                          </ListItemIcon>
                          <ListItemText primary="Search Listings" />
                        </NavigationItem>
                      )}
                      {user?.canSearchMarketplace() && (
                        <NavigationItem
                          to="/marketplace/saved-searches"
                          selected={location.pathname.startsWith("/marketplace/saved-searches")}
                          component={Link}
                          onClick={() => handleNavClose(temporary)}
                        >
                          <ListItemIcon>
                            <SavedSearchIcon />
                          </ListItemIcon>
                          <ListItemText primary="Saved Searches" />
                        </NavigationItem>
                      )}
                      <NavigationItem
                        to="/marketplace/bookmarks"
                        selected={location.pathname.startsWith("/marketplace/bookmarks")}
                        component={Link}
                        onClick={() => handleNavClose(temporary)}
                      >
                        <ListItemIcon>
                          <BookmarksOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Bookmarks" />
                      </NavigationItem>
                      <NavigationItem
                        to="/marketplace/profiles"
                        selected={location.pathname.startsWith("/marketplace/profiles")}
                        component={Link}
                        onClick={() => handleNavClose(temporary)}
                      >
                        <ListItemIcon>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Your Profiles" />
                      </NavigationItem>
                    </List>
                  </Collapse>
                </>
              )}
              {user?.canViewFirmOnboardingStatus() && (
                <NavigationItem
                  to="/firm-onboarding"
                  selected={isRouteWithinRoot(location.pathname, "firm-onboarding")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Firm Onboarding" />
                </NavigationItem>
              )}
              {user?.canAccessNetwork() && (
                <NavigationItem
                  to="/network"
                  selected={isRouteWithinRoot(location.pathname, "network")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <AccountTreeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Network" />
                </NavigationItem>
              )}
              <NavigationItem
                to={calendarRoute}
                selected={location.pathname.startsWith(calendarRoute)}
                component={Link}
                onClick={() => handleNavClose(temporary)}
              >
                <ListItemIcon>
                  <TodayIcon />
                </ListItemIcon>
                <ListItemText primary="Calendar" />
              </NavigationItem>
              {user?.canAccessAdministration() && (
                <>
                  <NavigationItem
                    to="/organization"
                    selected={location.pathname.startsWith("/organization")}
                    component={Link}
                    onClick={() => handleNavClose(temporary)}
                  >
                    <ListItemIcon>
                      <AdminPanelSettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Organization" />
                  </NavigationItem>
                  <NavigationItem
                    to="/permissions"
                    selected={location.pathname.startsWith("/permissions")}
                    component={Link}
                    onClick={() => handleNavClose(temporary)}
                  >
                    <ListItemIcon>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText primary="Permissions" />
                  </NavigationItem>
                </>
              )}
              {user?.canAdministerPlatform() && (
                <NavigationItem
                  to="/super-user"
                  selected={location.pathname.startsWith("/super-user")}
                  component={Link}
                  onClick={() => handleNavClose(temporary)}
                >
                  <ListItemIcon>
                    <SettingsAccessibility />
                  </ListItemIcon>
                  <ListItemText primary="Superuser Dashboard" />
                </NavigationItem>
              )}
              <NavigationItem
                onClick={() => setSupportMenuChoice(!supportMenuOpen)}
                aria-expanded={supportMenuOpen}
                selected={location.pathname.startsWith("/knowledge-base/attorney-hub")}
              >
                <ListItemIcon>
                  <ContactSupportIcon />
                </ListItemIcon>
                <ListItemText primary="Support" />
                {supportMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </NavigationItem>
              <Collapse
                in={supportMenuOpen}
                timeout="auto"
                unmountOnExit
                sx={{ backgroundColor: "#FAFAFA", pl: 2 }}
              >
                <List component="div" disablePadding>
                  <NavigationItem
                    to="/knowledge-base/attorney-hub"
                    selected={location.pathname.startsWith("/knowledge-base/attorney-hub")}
                    component={Link}
                    onClick={() => handleNavClose(temporary)}
                  >
                    <ListItemIcon>
                      <MenuBookIcon />
                    </ListItemIcon>
                    <ListItemText primary="Support Knowledge Base" />
                  </NavigationItem>
                  {/*
                    Kept as a direct mailto. Support has always been one click to an addressed email,
                    and routing it through a page first would cost that. The knowledge base article
                    above explains what to put in the message; this stays the way to send it.
                  */}
                  <NavigationItem href="mailto:support@attorneyhub.com" target="_top" component="a">
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText primary="Email Support" />
                  </NavigationItem>
                </List>
              </Collapse>
            </List>
          </NavContainer>
        </NavColumn>
        {!compact && user?.canAccessMarketplace() && (
          <MarketplaceLink to="/marketplace">
            <img src={attorneyHubMarketplaceLogo} alt="Attorney Hub Marketplace" />
          </MarketplaceLink>
        )}
      </NavDrawer>
    );
  }

  return (
    <CompactNavigationContext.Provider value={Boolean(props.compact && !showTemporaryDrawer)}>
      {drawer(showTemporaryDrawer)}
    </CompactNavigationContext.Provider>
  );
}
